import { ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'

import { IntegrationKey } from '@ankor-io/common/types/integrations/types'
import type { XeroSyncJobRunStepPayload } from '@ankor-io/common/types/integrations/xero/types'
import { XERO_STAGE_TO_STEP_TYPE, XeroConnectionSyncStage } from '@ankor-io/common/types/integrations/xero/types'
import { CopyToClipboard, Loading } from '@ankor-io/ui-components'

import { useRunSSE } from '@/hooks/useRunSSE'
import {
  useQuickBooksSyncJobRunQuery,
  useQuickBooksSyncJobRunStepsQuery,
  useXeroConnectionsQuery,
  useXeroSyncJobRunQuery,
  useXeroSyncJobRunStepsQuery,
} from '@/queries/organization'
import { formatDurationFromDates, formatTimestamp } from '@/utils/format'
import { highlightJson, tryParseStringFields } from '@/utils/json'

import { QUICKBOOKS_STAGE_TO_STEP_TYPE, STAGE_LABELS } from '../../../constants/integration'
import { BackHeader } from './integration-back-header'
import { statusBadge, stepStatusIcon } from './integration-status'

// ─── Helpers ────────────────────────────────────────────────────────────────

function hasExtraData(data: Record<string, unknown>): boolean {
  return Object.keys(data).some((k) => k !== 'message')
}

function getExtraData(data: Record<string, unknown>): Record<string, unknown> {
  const extra: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(data)) {
    if (key !== 'message') extra[key] = value
  }
  return extra
}

// ─── Step row ───────────────────────────────────────────────────────────────

function StepRow({ step }: { step: Pick<XeroSyncJobRunStepPayload, 'status' | 'createdAt' | 'completedAt' | 'data'> }) {
  const [expanded, setExpanded] = useState(false)
  const rawMessage = (step.data.message as string) ?? ''
  const message = (() => {
    try {
      const parsed = JSON.parse(rawMessage)
      return JSON.stringify(parsed, null, 2)
    } catch {
      return rawMessage
    }
  })()
  const showExpand = hasExtraData(step.data)
  const duration = formatDurationFromDates(step.createdAt, step.completedAt)
  const { highlighted, rawJson } = useMemo(() => {
    if (!showExpand) return { highlighted: null, rawJson: '' }
    const json = JSON.stringify(tryParseStringFields(getExtraData(step.data)), null, 2)
    return { highlighted: highlightJson(json), rawJson: json }
  }, [step.data, showExpand])

  return (
    <div className="border-b border-gray-800 last:border-b-0 ">
      <div
        className={`flex items-start gap-3 px-4 py-3 ${showExpand ? 'cursor-pointer hover:bg-gray-800/50' : ''}`}
        onClick={() => showExpand && setExpanded(!expanded)}
      >
        {/* Expand chevron or spacer */}
        <div className="w-4 shrink-0 pt-0.5">
          {showExpand && (
            <ChevronRight className={`size-4 text-gray-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          )}
        </div>

        {/* Status icon */}
        <div className="pt-0.5">{stepStatusIcon(step.status)}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="font-mono text-sm text-gray-200 whitespace-pre-wrap break-words">{message}</div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500 font-mono">
            <span>{formatTimestamp(step.createdAt)}</span>
            {step.completedAt && <span>→ {formatTimestamp(step.completedAt)}</span>}
            {duration && <span className="text-gray-400">({duration})</span>}
          </div>
        </div>
      </div>

      {/* Expanded data */}
      {expanded && showExpand && (
        <div className="px-4 p-3 pl-16">
          <div className="relative">
            <div className="absolute top-2 right-2 z-10">
              <CopyToClipboard text={rawJson} />
            </div>
            <pre
              className="text-xs font-mono text-gray-400 bg-gray-950 rounded-lg p-3 overflow-y-auto max-h-[80vh] no-scrollbar"
              dangerouslySetInnerHTML={{ __html: highlighted! }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main view ──────────────────────────────────────────────────────────────

export function IntegrationRunStepsView({
  organizationId,
  jobId,
  runId,
  stageKey,
  integrationKey,
  onBack,
}: {
  organizationId: string
  jobId: string
  runId: string
  stageKey: XeroConnectionSyncStage
  integrationKey: IntegrationKey
  onBack: () => void
}) {
  const isXero = integrationKey === IntegrationKey.XERO_EXPENSES
  const { data: connections } = useXeroConnectionsQuery(organizationId, { enabled: isXero })

  const connectionId = connections?.[0]?.id
  const xeroRun = useXeroSyncJobRunQuery(organizationId, connectionId, jobId, runId, { enabled: isXero })
  const quickBooksRun = useQuickBooksSyncJobRunQuery(organizationId, jobId, runId)

  const xeroStepType = XERO_STAGE_TO_STEP_TYPE[stageKey]
  const quickBooksStepType = QUICKBOOKS_STAGE_TO_STEP_TYPE[stageKey]
  const xeroSteps = useXeroSyncJobRunStepsQuery(organizationId, connectionId, jobId, runId, xeroStepType, {
    enabled: isXero,
  })
  const quickBooksSteps = useQuickBooksSyncJobRunStepsQuery(organizationId, jobId, runId, quickBooksStepType)
  const loaded = {
    [IntegrationKey.XERO_EXPENSES]: {
      name: 'Xero',
      run: xeroRun.data,
      steps: xeroSteps.data,
      isPending: xeroSteps.isPending,
      error: xeroSteps.error,
    },
    [IntegrationKey.QUICKBOOKS]: {
      name: 'QuickBooks',
      run: quickBooksRun.data,
      steps: quickBooksSteps.data,
      isPending: quickBooksSteps.isPending,
      error: quickBooksSteps.error,
    },
  }
  const { name, run, steps, isPending, error } = loaded[integrationKey]

  useRunSSE(organizationId, connectionId, jobId, runId, isXero && run?.status === 'IN_PROGRESS')

  const stageLabel = STAGE_LABELS[stageKey] ?? stageKey

  return (
    <div className="flex flex-col gap-4">
      <BackHeader
        label={`${name} — ${stageLabel}`}
        onBack={onBack}
      />

      {run && (
        <div className="flex items-center gap-4 px-4 py-2.5 mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">Stage</span>
            <span>{stageLabel}</span>
          </div>
          <div className="w-px h-5 bg-gray-200 dark:bg-gray-700" />
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">Run</span>
            <span className="font-mono">{run.id}</span>
          </div>
          <div className="w-px h-5 bg-gray-200 dark:bg-gray-700" />
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">Status</span>
            {statusBadge(run.status)}
          </div>
        </div>
      )}

      {isPending && <Loading variant="spinner" />}
      {error && <div className="text-sm text-red-500">Error: {error.message}</div>}

      {steps && steps.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">No steps found for this stage.</p>
      )}

      {steps && steps.length > 0 && (
        <div className="rounded-xl border border-gray-700 bg-gray-900 overflow-hidden">
          {/* Terminal header */}
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-800 border-b border-gray-700">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
            </div>
            <span className="text-xs font-mono text-gray-400 ml-2">
              {stageLabel} — {steps.length} steps
            </span>
          </div>

          {/* Steps list */}
          <div>
            {steps.map((step) => (
              <StepRow
                key={step.id}
                step={step}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
